import { Flex, MuiDatePicker } from '@pinchenterprisesnpm/friday-ui'
import { IScheduleSectionFormValues } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.types'
import { FormikProps } from 'formik'
import { endOfDay, format, isBefore, isSameDay } from 'date-fns'
import { DATE_FORMAT_STRING } from '@src/helpers/timeAndDateHelpers/constants'

import { CustomValidationError } from '@src/features/shift-post-v2/style'
import { useState } from 'react'
import {
  DateText,
  FloatingLabel,
  IconWrapper,
  LeftIconWrapper,
  StyledIcon,
  TriggerContainer,
  TriggerContent,
} from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/components/multi-single-day-picker/multi-single-day-picker.style'
import { faCalendar } from '@fortawesome/pro-solid-svg-icons'
import { faChevronDown } from '@fortawesome/pro-light-svg-icons'

const { DatePickerMultiple } = MuiDatePicker

export interface IScheduleSectionProps {
  formik: FormikProps<IScheduleSectionFormValues>
  isDisabled: boolean
  customWidth?: string
}

export const MultiSingleDayPicker = ({
  formik,
  isDisabled,
  customWidth,
}: IScheduleSectionProps) => {
  const { values, setFieldValue, errors } = formik
  const [isOpen, setIsOpen] = useState(false)

  const handleOnSingleDayClick = (day: any) => {
    const newSingleDayShifts = [...values.single_day_shifts]

    // Check if the day is already in the array
    const selectedIndex = newSingleDayShifts.findIndex(shiftDay =>
      isSameDay(shiftDay.date_start, day),
    )

    // If the day is not in the array, add it
    if (selectedIndex === -1) {
      newSingleDayShifts.push({
        id: format(new Date(day), DATE_FORMAT_STRING.MM_dd_yyyy),
        date_start: day,
        hour_start: values.hour_start,
        hour_end: values.hour_end,
        break: values.break,
        pay_value: values.pay_value,
      })

      // Ensure chronological order by date_start
      newSingleDayShifts.sort((a, b) => {
        const aTime = new Date(a.date_start).getTime()
        const bTime = new Date(b.date_start).getTime()
        return aTime - bTime
      })
    } else {
      newSingleDayShifts.splice(selectedIndex, 1)
    }

    setFieldValue('single_day_shifts', newSingleDayShifts)
  }

  const hasValue = values.single_day_shifts.length > 0
  const hasError =
    !!errors.single_day_shifts && typeof errors.single_day_shifts === 'string'

  // Create a function component for the trigger that accepts children
  const TriggerElement = ({ children, onClick, ...props }: any) => (
    <TriggerContainer
      isFocused={isOpen}
      hasError={hasError}
      onClick={isDisabled ? () => {} : onClick}
      isDisabled={isDisabled}
      customWidth={customWidth}
      {...props}
    >
      <FloatingLabel hasValue={hasValue} isFocused={isOpen}>
        Select Date
      </FloatingLabel>
      <TriggerContent>
        <LeftIconWrapper>
          <StyledIcon icon={faCalendar} />
        </LeftIconWrapper>
        {hasValue && <DateText>{children}</DateText>}
        <IconWrapper>
          <StyledIcon icon={faChevronDown} />
        </IconWrapper>
      </TriggerContent>
    </TriggerContainer>
  )

  return (
    <Flex flexDirection="column">
      <DatePickerMultiple
        triggerElement={<TriggerElement />}
        selectedDays={values.single_day_shifts.map(shift => shift.date_start)}
        disabledDays={(date: Date) => {
          return isBefore(endOfDay(date), endOfDay(new Date()))
        }}
        onDayClick={handleOnSingleDayClick}
        onReset={() => setFieldValue('single_day_shifts', [])}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        isDisabled={isDisabled}
      />

      {errors.single_day_shifts &&
      typeof errors.single_day_shifts === 'string' ? (
        <CustomValidationError mt="xs" mb={0}>
          {errors.single_day_shifts}
        </CustomValidationError>
      ) : null}
    </Flex>
  )
}
